/**
 * GenericDetailContent — Rich detail page content for any doctype, designed
 * to render inside the slide-over panel shell (GenericDetailPanel).
 *
 * Replicates the core rendering from StandardDetailPage but takes props
 * instead of reading route params, and skips PageContainer chrome.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DetailView } from "@/components/layout/DetailView";
import { DetailStickyTopBar } from "@/components/layout/DetailStickyTopBar";
import { DetailPageTabBar } from "@/components/layout/DetailPageTabBar";
import { useGetDoc, useUpdateDoc, useDeleteDoc } from "@/lib/hooks/useFrappeQuery";
import { useDocSidebar } from "@/features/common/api/useDocSidebar";
import { InlineAssignments } from "@/features/common/components/InlineAssignments";
import { InlineShareActions } from "@/features/common/components/InlineShareActions";
import { HeaderMediaThumbnail } from "@/features/inventory/item-detail/components/HeaderMediaThumbnail";
import { HeaderActions } from "@/features/inventory/item-detail/components/HeaderActions";
import { DocStatusBadge } from "@/components/ui/doc-status-badge";
import { useDocWorkflowHandlers } from "@/hooks/useDocWorkflowHandlers";
import { erp } from "@/lib/erp";
import { isDirty } from "@/features/inventory/item-detail/utils/computeDirtyState";
import { useAuthStore } from "@/lib/store/authStore";
import { usePreferencesStore } from "@/lib/store/preferencesStore";
import { prepareDocForSave } from "@/lib/erp/save";
import { pruneLocalPlaceholderChildRowsInDoc } from "@/components/child-table/utils/prune";
import { validateDocForm } from "@/features/doc/validateDocForm";
import { resolveView } from "@/lib/views/registry";
import { SystemView } from "@/components/view/SystemView";
import { ExperienceView } from "@/components/view/ExperienceView";
import type { DocTypeSchema } from "@/lib/erp/meta";
import type { OrderContext } from "@/lib/views/types";
import { hasDocViewSchema, getDocViewSchema } from "@/lib/views/docViewSchemas";
import { buildListRoute } from "@/lib/config/docRoutes";
import { useCanQuery } from "@/hooks/useCanQuery";
import { useRegisterActiveDetailPage } from "@/hooks/useRegisterActiveDetailPage";
import { DetailPageSkeleton } from "@/features/doc/DetailPageSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { logger } from "@/lib/utils/logger";
import { cn } from "@/lib/utils";
import { ChevronLeft, Loader2 } from "lucide-react";
import { LayoutModeSwitcher, DETAIL_PANEL_MODE_KEY, type DetailPanelMode } from "@/features/list/GenericDetailPanel";
import { StorefrontOrderBanner } from "@/features/selling/sales-order-detail/StorefrontOrderBanner";
import { OrderDetailSidebar } from "@/features/selling/sales-order-detail/OrderDetailSidebar";
import { ItemDetailSidebar } from "@/features/inventory/item-detail/components/ItemDetailSidebar";
import { PricingRulePromotionSummary } from "@/features/inventory/pricing-rule/PricingRulePromotionSummary";
import { PricingRuleDetailSidebar } from "@/features/inventory/pricing-rule/PricingRuleDetailSidebar";
import { CouponPerformancePanel } from "@/features/inventory/pricing-rule/CouponPerformancePanel";
import { BookingDetailContent } from "@/features/booking/components/BookingDetailContent";
import { BookingResourceConfigDetailContent } from "@/features/booking/components/BookingResourceConfigDetailContent";
import { DocChatPanel } from "@/features/common/components/DocChatPanel";
import { createSyntheticDocTypeView } from "@/lib/views/utils/createSyntheticDocTypeView";
import { getDocTitle } from "@/lib/erp/docTitle";
import { useDetailPageTabs } from "@/contexts/DetailPageTabsContext";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface GenericDetailContentProps {
  doctype: string;
  docName: string;
  onClose?: () => void;
}

export function GenericDetailContent({
  doctype,
  docName,
}: GenericDetailContentProps) {
  const { isAuthenticated } = useAuthStore();
  const { activeTab } = useDetailPageTabs();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParamsGD] = useSearchParams();
  // Full-page ("Full-screen") mode is the only place with ?generic=1. Panel
  // mode (slide-over/modal) opens via ?detail= on the list route. In full-page
  // mode we render our own sticky top bar (mirroring the panel) and suppress
  // the header backNav pill to avoid a duplicate back button.
  const isFullPage = searchParamsGD.get("generic") === "1";
  const scrollRef = useRef<HTMLDivElement>(null);
  const [uiError, setUiError] = useState<string | null>(null);
  const [docVersion, setDocVersion] = useState(0);
  const [showChat, setShowChat] = useState(false);

  // ------ Data fetching ------
  const {
    data: docData,
    isLoading: isLoadingDoc,
    error: docError,
  } = useGetDoc(doctype, docName, {
    enabled: !!docName && isAuthenticated,
  });

  const sidebarQuery = useDocSidebar(doctype, docName || undefined);

  const { data: metaSchema, isLoading: isLoadingMeta } = useQuery({
    queryKey: ["meta", doctype],
    queryFn: () => erp.meta.getDocTypeSchema(doctype),
    enabled: isAuthenticated && !!doctype,
  });

  // ------ Form state ------
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [originalData, setOriginalData] = useState<Record<string, any>>({});
  // Register the open record (dev autofill + page-aware AI); reads this panel's own tab.
  useRegisterActiveDetailPage({ doctype, metaSchema, formData, setFormData, savedData: originalData });
  const hasHydratedRef = useRef(false);
  const currentDocRef = useRef<string>("");
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (currentDocRef.current !== docName) {
      currentDocRef.current = docName;
      hasHydratedRef.current = false;
    }
    if (docData) {
      if (!hasHydratedRef.current) {
        setFormData(docData);
        setOriginalData(docData);
        setDocVersion(0);
        hasHydratedRef.current = true;
        return;
      }
      if (isSavingRef.current) return;
      if (isDirty(originalData, formData)) return;
      // Server data changed (e.g., personalization edit) — sync to form
      setFormData(docData);
      setOriginalData(docData);
    }
  }, [docData, docName, originalData, formData]);

  const resetKey = `${docName}-${docVersion}`;

  // ------ View resolution ------
  const viewMode = usePreferencesStore((s) => s.viewMode);
  const devMode = usePreferencesStore((s) => s.devMode);
  const experienceStyle = usePreferencesStore((s) => s.experienceStyle);
  const systemMode = devMode ? viewMode === "system" : false;

  const resolvedView = useMemo(() => {
    if (!metaSchema) return null;
    const preferredStyle =
      !systemMode && experienceStyle !== "auto" ? experienceStyle : undefined;
    const view = resolveView(
      metaSchema.name,
      undefined as OrderContext | undefined,
      systemMode,
      devMode,
      preferredStyle,
    );
    if (!systemMode && view.mode === "system" && hasDocViewSchema(doctype)) {
      const schema = getDocViewSchema(doctype);
      const syntheticView = createSyntheticDocTypeView(doctype, schema);
      return {
        mode: "experience" as const,
        view: syntheticView,
        style: syntheticView.style,
      };
    }
    return view;
  }, [metaSchema, doctype, systemMode, devMode, experienceStyle]);

  // ------ Permissions ------
  const { allowed: canWrite } = useCanQuery(doctype, "write", docName);
  const { allowed: canDelete } = useCanQuery(doctype, "delete", docName);

  // ------ Mutations ------
  const updateMutation = useUpdateDoc<Record<string, any>>(doctype, docName, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["get", doctype, docName] });
      queryClient.invalidateQueries({ queryKey: ["list", doctype] });
      queryClient.invalidateQueries({ queryKey: ["doctype-list", doctype] });
    },
  });

  const deleteMutation = useDeleteDoc(doctype, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["list", doctype] });
      queryClient.invalidateQueries({ queryKey: ["doctype-list", doctype] });
    },
  });

  // ------ Workflow ------
  const workflow = useDocWorkflowHandlers({
    doctype,
    name: docName,
    formData,
    isSubmittable: !!metaSchema?.is_submittable,
    permissions: metaSchema?.permissions,
    onMutationSuccess: () => {
      hasHydratedRef.current = false;
    },
  });
  const isReadOnly = (formData.docstatus ?? 0) >= 1;

  // ------ Effective meta (read-only autoname field) ------
  const effectiveMetaSchema = useMemo(() => {
    if (!metaSchema) return metaSchema;
    const autoname = metaSchema.autoname;
    if (!autoname || !autoname.startsWith("field:")) return metaSchema;
    if (metaSchema.allow_rename) return metaSchema;
    const fieldname = autoname.split(":")[1];
    if (!fieldname) return metaSchema;
    return {
      ...metaSchema,
      fields: metaSchema.fields.map((f: any) =>
        f.fieldname === fieldname ? { ...f, read_only: 1 } : f,
      ),
    } as DocTypeSchema;
  }, [metaSchema]);

  // ------ Dirty state ------
  const dirtyState = useMemo(
    () => isDirty(originalData, formData),
    [formData, originalData],
  );

  const handleFieldChange = (fieldname: string, value: any) => {
    setFormData((prev) => ({ ...prev, [fieldname]: value }));
  };

  // ------ Validate + Save ------
  const validateForm = useCallback(
    async (dataToValidate?: Record<string, any>) => {
      if (!metaSchema) {
        return { valid: false, errors: ["Schema not loaded"] };
      }
      return validateDocForm(dataToValidate ?? formData, metaSchema, queryClient);
    },
    [formData, metaSchema, queryClient],
  );

  const handleSave = async (dataOverride?: Record<string, any>) => {
    if (!metaSchema) {
      setUiError("Cannot save: metadata not loaded");
      return;
    }
    if (
      dataOverride &&
      ("type" in dataOverride || "nativeEvent" in dataOverride)
    ) {
      dataOverride = undefined;
    }
    const dataToSave = dataOverride ?? formData;
    const validation = await validateForm(dataToSave);
    if (!validation.valid) {
      setUiError(`Validation errors:\n${validation.errors.join("\n")}`);
      return;
    }
    const docToSaveRaw = prepareDocForSave(dataToSave, metaSchema);
    const docToSave = pruneLocalPlaceholderChildRowsInDoc(
      docToSaveRaw,
      metaSchema,
      (childDoctype) =>
        queryClient.getQueryData<DocTypeSchema>(["meta", childDoctype]),
    );
    isSavingRef.current = true;
    try {
      const updatedDoc = await updateMutation.mutateAsync(docToSave);
      setFormData(updatedDoc);
      setOriginalData(updatedDoc);
      setDocVersion((v) => v + 1);
      if (updatedDoc.name) {
        queryClient.setQueryData(
          ["get", doctype, updatedDoc.name],
          updatedDoc,
        );
      }
    } catch (error) {
      logger.error("[GenericDetailContent] Save error:", error);
      const msg = error instanceof Error ? error.message : "Unknown error";
      setUiError(`Failed to save: ${msg}`);
    } finally {
      isSavingRef.current = false;
    }
  };

  const handleSaveWithPatch = async (patch: Record<string, any>) => {
    if (!patch || Object.keys(patch).length === 0) return;
    const prev = formData;
    const merged = { ...prev, ...patch };
    setFormData(merged);
    isSavingRef.current = true;
    try {
      const validation = await validateForm(merged);
      if (!validation.valid) {
        setFormData(prev);
        throw new Error(`Validation: ${validation.errors.join(", ")}`);
      }
      await handleSave(merged);
    } catch (error) {
      logger.error("[GenericDetailContent] Patch save failed:", error);
      setFormData(prev);
      throw error;
    } finally {
      isSavingRef.current = false;
    }
  };

  // ------ Loading / Error states ------
  if (isLoadingMeta || isLoadingDoc) {
    return (
      <div className="px-4 py-6">
        <DetailPageSkeleton doctype={doctype} />
      </div>
    );
  }

  if (docError) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-destructive">Failed to load document</p>
      </div>
    );
  }

  if (!metaSchema) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-destructive">Failed to load schema</p>
      </div>
    );
  }

  // ------ Two-column layout for Sales Order / Item / Booking Details tab ------
  const isSalesOrderDetailsTab =
    doctype === "Sales Order" && (!activeTab || activeTab === "details");
  const isItemDetailsTab =
    doctype === "Item" && (!activeTab || activeTab === "details");
  const isBookingDetailsTab =
    doctype === "Booking" && (!activeTab || activeTab === "details");
  const isBookingResourceConfigDetailsTab =
    doctype === "Booking Resource Config" && (!activeTab || activeTab === "details");
  // Pricing Rule (coupon) — render the bespoke promotion chrome (summary hero +
  // sidebar) in the panel, same as its full custom page (PricingRuleDetailPage).
  const isPricingRuleDetailsTab =
    doctype === "Pricing Rule" && (!activeTab || activeTab === "details");
  const isTwoColumnTab = isSalesOrderDetailsTab || isItemDetailsTab || isBookingDetailsTab || isPricingRuleDetailsTab;

  // ------ Derived data ------
  const sidebarData = sidebarQuery.data;
  const docImage = sidebarData?.image || formData?.image || null;
  const pageTitle = getDocTitle(metaSchema, docData) ?? docName;

  return (
    <>
    {isFullPage && (
      // Shared sticky top bar (same chrome as bespoke StandardDetailPage detail
      // pages). For Sales Orders the bar shows the order id (docName) rather than
      // the title_field (customer name). The layout-mode switcher lets you pop
      // back into the slide-over / modal panel.
      <DetailStickyTopBar
        title={doctype === "Sales Order" ? docName : pageTitle}
        onBack={() => navigate(buildListRoute(doctype))}
        onClose={() => navigate(buildListRoute(doctype))}
        rightExtra={
          <LayoutModeSwitcher
            mode="fullscreen"
            onModeChange={(m: DetailPanelMode) => {
              if (m === "fullscreen") return; // already here
              try { localStorage.setItem(DETAIL_PANEL_MODE_KEY, m); } catch { /* ignore */ }
              navigate(`${buildListRoute(doctype)}?detail=${encodeURIComponent(docName)}`);
            }}
          />
        }
      />
    )}
    <DetailView
      scrollRef={scrollRef}
      hasStickyHeaderBar={isFullPage}
      contentClassName={isFullPage ? "px-0" : "px-4"}
      title={pageTitle}
      titleBadge={
        metaSchema?.is_submittable ? (
          <DocStatusBadge docstatus={formData.docstatus ?? 0} />
        ) : undefined
      }
      media={
        docImage ? (
          <HeaderMediaThumbnail image={docImage} video={null} alt={pageTitle} />
        ) : undefined
      }
      backNav={
        // In full-page mode the sticky top bar already has the back button, so
        // suppress this pill to avoid a duplicate back button.
        isFullPage ? undefined : (
          <Link
            to={buildListRoute(doctype)}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-card h-8 pl-2 pr-3 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>{doctype}</span>
          </Link>
        )
      }
      actions={
        <HeaderActions
          onSave={handleSave}
          onPermanentDelete={canDelete ? () => { if (docName) deleteMutation.mutate(docName); } : undefined}
          isSaving={updateMutation.isPending}
          isDirty={dirtyState}
          isNew={false}
          itemName={docName}
          doctype={doctype}
          deleteLabel={doctype.toLowerCase()}
          attachmentCount={sidebarData?.attachments?.length ?? 0}
          docstatus={formData.docstatus ?? 0}
          isSubmittable={!!metaSchema?.is_submittable}
          canSubmit={workflow.canSubmit}
          canCancel={workflow.canCancel}
          canAmend={workflow.canAmend}
          onDocSubmit={workflow.handleDocSubmit}
          onDocCancel={workflow.handleDocCancel}
          onDocAmend={workflow.handleDocAmend}
          isSubmitting={workflow.isSubmitting}
          isCancelling={workflow.isCancelling}
          isAmending={workflow.isAmending}
        />
      }
      tabBar={<DetailPageTabBar />}
      belowTitle={
        sidebarData ? (
          <div className="flex items-center gap-3">
            <InlineAssignments
              doctype={doctype}
              name={docName}
              assigned_to={sidebarData.assigned_to}
              editable={canWrite}
            />
          </div>
        ) : (
          <Skeleton className="h-9 w-9 rounded-full" />
        )
      }
      belowActions={
        sidebarData ? (
          <InlineShareActions
            doctype={doctype}
            name={docName}
            shares={sidebarData.shares}
            public_share={sidebarData.public_share}
            share_capabilities={sidebarData.share_capabilities}
            liked_by={sidebarData.liked_by}
            like_count={sidebarData.like_count}
            comment_count={sidebarData.comment_count}
            followed={sidebarData.is_followed}
            tagCount={sidebarData.tags?.length ?? 0}
            editable={canWrite}
            onComment={() => setShowChat(!showChat)}
          />
        ) : (
          <Skeleton className="h-5 w-[340px] rounded" />
        )
      }
    >
      {uiError && (
        <div className="mx-4 mb-4 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <button
            type="button"
            onClick={() => setUiError(null)}
            className="float-right text-destructive/70 hover:text-destructive"
          >
            ×
          </button>
          <pre className="whitespace-pre-wrap">{uiError}</pre>
        </div>
      )}
      {/* Booking / Resource Config: fully custom detail layout */}
      {isBookingDetailsTab ? (
        <BookingDetailContent formData={formData} onRefresh={() => queryClient.invalidateQueries({ queryKey: ["get", doctype, docName] })} />
      ) : isBookingResourceConfigDetailsTab ? (
        <BookingResourceConfigDetailContent formData={formData} onFieldChange={handleFieldChange} />
      ) : (
      /* Two-column layout wrapper for Sales Order / Item Details tab */
      <div className={cn(
        isTwoColumnTab && "md:flex md:gap-6",
      )}>
        {/* Main content column */}
        <div className={cn(isTwoColumnTab && "md:flex-1 md:min-w-0")}>
          {doctype === "Sales Order" && docName && (!activeTab || activeTab === "details") && (
            <>
              <StorefrontOrderBanner formData={formData} salesOrderName={docName} />
            </>
          )}
          {isPricingRuleDetailsTab && docName && (
            <div className="space-y-6">
              <CouponPerformancePanel formData={formData} docName={docName} />
              <PricingRulePromotionSummary formData={formData} docName={docName} />
            </div>
          )}
          {resolvedView ? (
            resolvedView.mode === "system" ? (
              <SystemView
                meta={effectiveMetaSchema!}
                formData={formData}
                onFieldChange={handleFieldChange}
                disabled={
                  !canWrite || updateMutation.isPending || isReadOnly
                }
                onSave={handleSave}
                onSubmit={handleSaveWithPatch}
                isSaving={updateMutation.isPending}
                resetKey={resetKey}
                documentName={docName}
              />
            ) : resolvedView.view ? (
              <ExperienceView
                view={resolvedView.view}
                meta={effectiveMetaSchema!}
                formData={formData}
                onFieldChange={handleFieldChange}
                disabled={
                  !canWrite || updateMutation.isPending || isReadOnly
                }
                onSave={handleSave}
                onSubmit={handleSaveWithPatch}
                isSaving={updateMutation.isPending}
                resetKey={resetKey}
                documentName={docName}
                isNew={false}
              />
            ) : (
              <div className="text-muted-foreground py-4 px-4">
                No view available
              </div>
            )
          ) : (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
        {/* Right sidebar — rendered on Sales Order / Item Details tab */}
        {isSalesOrderDetailsTab && (
          <div className="w-full md:w-[380px] shrink-0">
            <OrderDetailSidebar formData={formData} />
          </div>
        )}
        {isItemDetailsTab && (
          <div className="w-full md:w-[380px] shrink-0">
            <ItemDetailSidebar formData={formData} />
          </div>
        )}
        {isPricingRuleDetailsTab && docName && (
          <div className="w-full md:w-[360px] shrink-0">
            <PricingRuleDetailSidebar formData={formData} docName={docName} />
          </div>
        )}
      </div>
      )}
    </DetailView>

    {/* Chat slide-in sheet (portal to body) */}
    {createPortal(
      <>
        {/* Backdrop */}
        <div
          className={cn(
            "fixed inset-0 z-[60] bg-black/40 transition-opacity duration-300",
            showChat ? "opacity-100" : "opacity-0 pointer-events-none",
          )}
          onClick={() => setShowChat(false)}
        />
        {/* Sheet */}
        <div
          className={cn(
            "fixed top-0 right-0 z-[61] h-full w-full max-w-md bg-background shadow-2xl border-l border-border/40 flex flex-col transition-transform duration-300 ease-out",
            showChat ? "translate-x-0" : "translate-x-full",
          )}
        >
          {showChat && (
            <div className="h-full [&>div]:!max-h-full [&>div]:!rounded-none [&>div]:!border-0 [&>div]:!shadow-none">
              <DocChatPanel
                doctype={doctype}
                name={docName}
                onClose={() => setShowChat(false)}
              />
            </div>
          )}
        </div>
      </>,
      document.body,
    )}
    </>
  );
}
